import React, { memo, useEffect, useState } from 'react';
import {
    Table,
    Input,
    Switch,
    Tabs,
    Spin,
    Alert,
    Badge,
    Tooltip,
    Typography,
    InputNumber,
    Select,
    Space,
    Button,
    Divider,
    Card,
    Row,
    Col,
    Collapse
} from 'antd';
import { 
    InfoCircleOutlined, 
    EyeOutlined, 
    EyeInvisibleOutlined,
    StarOutlined,
    StarFilled,
    ReloadOutlined,
    SaveOutlined,
    FileOutlined,
    SettingOutlined,
    AppstoreOutlined,
    OrderedListOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { CustomerType, FieldGroupDTO, FieldMetadataDTO, GroupedFieldConfigurationDTO } from '../../../../shared/api/model';
import { 
    useGetGroupedFieldConfiguration,
    useGetFieldConfigurationForCategoryGrouped
} from '@/shared/api/client/field-configuration-controller';


const { Text, Title } = Typography;
const { Option } = Select;

interface FieldConfigurationTabProps {
    customerType: CustomerType;
    categoryId?: number; // Category ID for loading existing configurations
    groupedFieldConfiguration?: GroupedFieldConfigurationDTO; // Use grouped structure instead of flat array
    mode: 'create' | 'edit' | 'view';
    onFieldPropertyChange: (fieldName: string, entityType: string, prop: string, value: any) => void;
    onConfigurationChange?: (configuration: GroupedFieldConfigurationDTO) => void; // Notify parent of configuration changes
}

const FieldConfigurationTab: React.FC<FieldConfigurationTabProps> = memo(({
    customerType,
    categoryId,
    groupedFieldConfiguration,
    mode,
    onFieldPropertyChange,
    onConfigurationChange
}) => {
    const [activeTabKey, setActiveTabKey] = useState<string>('');
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [currentConfiguration, setCurrentConfiguration] = useState<GroupedFieldConfigurationDTO | null>(null);
    const [isUsingTemplate, setIsUsingTemplate] = useState(true);

    // Template configuration (fallback)
    const {
        data: templateConfigRes,
        isLoading: isLoadingTemplate,
        error: templateError,
        refetch: refetchTemplate
    } = useGetGroupedFieldConfiguration(customerType, {
        query: {
            enabled: !!customerType && mode !== 'create'
        }
    });

    // Category-specific configuration (if categoryId exists)
    const {
        data: categoryConfigRes,
        isLoading: isLoadingCategory,
        error: categoryError,
        refetch: refetchCategory
    } = useGetFieldConfigurationForCategoryGrouped(categoryId || 0, {
        query: {
            enabled: !!categoryId && mode !== 'create'
        }
    });

    // Field configuration is now saved as part of customer category create/update operations

    const templateConfig = templateConfigRes;
    const categoryConfig = categoryConfigRes;
    
    // Determine which configuration to use
    const groupedFieldConfig = currentConfiguration || categoryConfig || templateConfig;
    const isLoading = isLoadingTemplate || isLoadingCategory;
    const error = templateError || categoryError;

    useEffect(() => {
        // Debug logging
        console.log('FieldConfigurationTab - Debug Info:', {
            customerType,
            categoryId,
            isLoading,
            error,
            groupedFieldConfig,
            fieldGroupsLength: groupedFieldConfig?.fieldGroups?.length,
            fieldGroups: groupedFieldConfig?.fieldGroups,
            isUsingTemplate,
            hasCustomConfig: !!categoryConfig,
            passedGroupedConfig: groupedFieldConfiguration
        });

        // Priority: Use passed grouped configuration first, then fetched data
        const configToUse = groupedFieldConfiguration || categoryConfig || templateConfig;
        const fieldGroups = configToUse?.fieldGroups ?? [];
        
        if (configToUse && configToUse !== currentConfiguration) {
            setCurrentConfiguration(configToUse);
            setIsUsingTemplate(!groupedFieldConfiguration && !categoryConfig);
        }

        // Set the first visible group as active tab when data loads
        if (fieldGroups.length > 0 && !activeTabKey) {
            const firstVisibleGroup = fieldGroups.find(group => group.isVisible);
            if (firstVisibleGroup) {
                setActiveTabKey(firstVisibleGroup.groupKey || '');
            }
        }
    }, [groupedFieldConfiguration, groupedFieldConfig, activeTabKey, customerType, categoryId, isLoading, error, categoryConfig, templateConfig, currentConfiguration, isUsingTemplate]);

    // Handler functions for enhanced functionality
    const handleFetchTemplate = () => {
        console.log('=== FETCH TEMPLATE DEBUG ===');
        console.log('Template Config Available:', !!templateConfig);
        console.log('Customer Type:', customerType);
        console.log('Mode:', mode);
        
        // Frontend-only action: fetch fresh template and allow customization
        if (templateConfig) {
            console.log('Using existing template config');
            const templateCopy = { ...templateConfig };
            setCurrentConfiguration(templateCopy);
            setIsUsingTemplate(true);
            
            // Convert template to form fields and update parent state
            if (onConfigurationChange) {
                onConfigurationChange(templateCopy);
                console.log('Notified parent of template configuration');
            }
            
            console.log('Fetched fresh template configuration for customization');
        } else {
            console.log('Template not available, refetching...');
            // Refetch template if not available
            refetchTemplate();
        }
        console.log('=== END FETCH TEMPLATE DEBUG ===');
    };

    const handleConfigurationChange = (updatedConfig: GroupedFieldConfigurationDTO) => {
        setCurrentConfiguration(updatedConfig);
        setIsUsingTemplate(false);
        // Notify parent component of configuration changes
        if (onConfigurationChange) {
            onConfigurationChange(updatedConfig);
        }
    };

    // UI Configuration Handlers
    const handleGroupVisibilityChange = (groupKey: string, isVisible: boolean) => {
        console.log('=== GROUP VISIBILITY CHANGE ===');
        console.log('Group Key:', groupKey);
        console.log('New Visibility:', isVisible);
        
        if (currentConfiguration) {
            const updatedConfig = {
                ...currentConfiguration,
                fieldGroups: currentConfiguration.fieldGroups?.map(group => 
                    group.groupKey === groupKey 
                        ? { ...group, isVisible }
                        : group
                )
            };
            
            console.log('Updated Configuration:', updatedConfig);
            handleConfigurationChange(updatedConfig);
        }
    };

    const handleGroupRenderingModeChange = (groupKey: string, renderingMode: string) => {
        console.log('=== GROUP RENDERING MODE CHANGE ===');
        console.log('Group Key:', groupKey);
        console.log('New Rendering Mode:', renderingMode);
        
        if (currentConfiguration) {
            const updatedConfig = {
                ...currentConfiguration,
                fieldGroups: currentConfiguration.fieldGroups?.map(group => 
                    group.groupKey === groupKey 
                        ? { 
                            ...group, 
                            uiConfig: { 
                                ...group.uiConfig, 
                                renderingMode 
                            } 
                        }
                        : group
                )
            };
            
            console.log('Updated Configuration:', updatedConfig);
            handleConfigurationChange(updatedConfig);
        }
    };

    const handleGroupWizardOrientationChange = (groupKey: string, wizardOrientation: string) => {
        console.log('=== GROUP WIZARD ORIENTATION CHANGE ===');
        console.log('Group Key:', groupKey);
        console.log('New Wizard Orientation:', wizardOrientation);
        
        if (currentConfiguration) {
            const updatedConfig = {
                ...currentConfiguration,
                fieldGroups: currentConfiguration.fieldGroups?.map(group => 
                    group.groupKey === groupKey 
                        ? { 
                            ...group, 
                            uiConfig: { 
                                ...group.uiConfig, 
                                wizardOrientation 
                            } 
                        }
                        : group
                )
            };
            
            console.log('Updated Configuration:', updatedConfig);
            handleConfigurationChange(updatedConfig);
        }
    };

    const handleGroupProgressChange = (groupKey: string, showProgress: boolean) => {
        console.log('=== GROUP PROGRESS CHANGE ===');
        console.log('Group Key:', groupKey);
        console.log('Show Progress:', showProgress);
        
        if (currentConfiguration) {
            const updatedConfig = {
                ...currentConfiguration,
                fieldGroups: currentConfiguration.fieldGroups?.map(group => 
                    group.groupKey === groupKey 
                        ? { 
                            ...group, 
                            uiConfig: { 
                                ...group.uiConfig, 
                                showProgress 
                            } 
                        }
                        : group
                )
            };
            
            console.log('Updated Configuration:', updatedConfig);
            handleConfigurationChange(updatedConfig);
        }
    };

    // Note: Global UI Configuration (rendering mode, wizard orientation, etc.) 
    // is now managed at the category level in the "Customer Type Config" tab,
    // not at the field configuration level. This provides a single source of truth
    // for UI settings that apply across the entire category.

    const handleFieldPropertyChange = (fieldName: string, entityType: string, prop: string, value: any) => {
        // DEBUG: Log field property changes
        console.log('=== FIELD PROPERTY CHANGE DEBUG ===');
        console.log('Field Name:', fieldName);
        console.log('Entity Type:', entityType);
        console.log('Property:', prop);
        console.log('New Value:', value);
        console.log('Current Configuration:', currentConfiguration);
        console.log('Grouped Field Config:', groupedFieldConfig);
        console.log('Is Using Template:', isUsingTemplate);
        
        // Call the parent's onChange handler
        onFieldPropertyChange(fieldName, entityType, prop, value);
        console.log('Called parent onFieldPropertyChange');
        
        // Update the current configuration state to reflect changes
        if (groupedFieldConfig) {
            const updatedConfig = { ...groupedFieldConfig };
            
            // Find and update the field in the configuration
            updatedConfig.fieldGroups = updatedConfig.fieldGroups?.map(group => ({
                ...group,
                fields: group.fields?.map(field => {
                    if (field.fieldName === fieldName) {
                        const updatedField = {
                            ...field,
                            [prop]: value
                        };
                        console.log('Updated field:', updatedField);
                        return updatedField;
                    }
                    return field;
                }) || []
            })) || [];
            
            console.log('Updated configuration:', updatedConfig);
            setCurrentConfiguration(updatedConfig);
            setIsUsingTemplate(false);
            
            // Notify parent component of configuration changes
            if (onConfigurationChange) {
                onConfigurationChange(updatedConfig);
                console.log('Called parent onConfigurationChange');
            }
        }
        console.log('=== END FIELD PROPERTY CHANGE DEBUG ===');
    };

    const getFieldValue = (fieldName: string, property: string, defaultValue: any) => {
        // First check if we have a current configuration with this field
        if (currentConfiguration?.fieldGroups) {
            for (const group of currentConfiguration.fieldGroups) {
                const field = group.fields?.find(f => f.fieldName === fieldName);
                if (field && field[property as keyof FieldMetadataDTO] !== undefined) {
                    return field[property as keyof FieldMetadataDTO];
                }
            }
        }
        
        // No fallback needed - all data should come from grouped configuration
        return defaultValue;
    };

    const getTypeColor = (type: string): string => {
        const colorMap: Record<string, string> = {
            'text': 'blue',
            'number': 'green',
            'date': 'orange',
            'select': 'purple',
            'email': 'cyan',
            'phone': 'magenta',
            'url': 'lime',
            'textarea': 'gold'
        };
        return colorMap[type] || 'default';
    };

    const getTypeIcon = (type: string) => {
        const iconMap: Record<string, React.ReactNode> = {
            'text': '📝',
            'number': '🔢',
            'date': '📅',
            'select': '📋',
            'email': '📧',
            'phone': '📞',
            'url': '🌐',
            'textarea': '📄'
        };
        return iconMap[type] || '📝';
    };

    const renderFieldTable = (fields: FieldMetadataDTO[], groupKey: string) => {
        const fieldColumns = [
            {
                title: 'Field Information',
                key: 'fieldInfo',
                width: 250,
                render: (_: any, record: FieldMetadataDTO) => (
                    <div>
                        <Space align="start">
                            <span style={{ fontSize: '16px' }}>
                                {getTypeIcon(record.type || 'text')}
                            </span>
                            <div>
                                <Text strong style={{ fontSize: '14px' }}>
                                    {record.label}
                                </Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {record.fieldName}
                                </Text>
                                <br />
                                <Badge 
                                    color={getTypeColor(record.type || 'text')} 
                                    text={record.type?.toUpperCase()} 
                                    style={{ fontSize: '10px' }}
                                />
                                {record.selectProvider && (
                                    <Badge 
                                        color="purple" 
                                        text={`Provider: ${record.selectProvider}`}
                                        style={{ fontSize: '10px', marginLeft: 4 }}
                                    />
                                )}
                            </div>
                        </Space>
                    </div>
                )
            },
            {
                title: 'Configuration',
                key: 'configuration',
                width: 300,
                render: (_: any, record: FieldMetadataDTO) => (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>Custom Label:</Text>
                            <Input
                                value={getFieldValue(record.fieldName || '', 'label', record.label)}
                                onChange={(e) =>
                                    handleFieldPropertyChange(
                                        record.fieldName || '',
                                        'Customer',
                                        'label',
                                        e.target.value
                                    )
                                }
                                disabled={mode === 'view'}
                                placeholder={record.placeholder}
                                size="small"
                            />
                        </div>
                        {showAdvancedOptions && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>Display Order:</Text>
                                <InputNumber
                                    value={getFieldValue(record.fieldName || '', 'displayOrder', record.displayOrder)}
                                    onChange={(value) =>
                                        handleFieldPropertyChange(
                                            record.fieldName || '',
                                            'Customer',
                                            'displayOrder',
                                            value
                                        )
                                    }
                                    disabled={mode === 'view'}
                                    size="small"
                                    min={0}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        )}
                    </Space>
                )
            },
            {
                title: 'Visibility & Requirements',
                key: 'settings',
                width: 200,
                render: (_: any, record: FieldMetadataDTO) => (
                    <Space direction="vertical" align="center">
                        <div>
                            <Tooltip title="Whether this field appears in forms">
                                <Switch
                                    checked={getFieldValue(record.fieldName || '', 'isVisible', record.isVisible ?? true)}
                                    onChange={(checked) =>
                                        handleFieldPropertyChange(
                                            record.fieldName || '',
                                            'Customer',
                                            'isVisible',
                                            checked
                                        )
                                    }
                                    disabled={mode === 'view'}
                                    checkedChildren={<EyeOutlined />}
                                    unCheckedChildren={<EyeInvisibleOutlined />}
                                />
                            </Tooltip>
                            <br />
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                Visible
                            </Text>
                        </div>
                        <div>
                            <Tooltip title="Whether this field is mandatory">
                                <Switch
                                    checked={getFieldValue(record.fieldName || '', 'defaultRequired', record.defaultRequired ?? false)}
                                    onChange={(checked) =>
                                        handleFieldPropertyChange(
                                            record.fieldName || '',
                                            'Customer',
                                            'defaultRequired',
                                            checked
                                        )
                                    }
                                    disabled={mode === 'view'}
                                    checkedChildren={<StarFilled />}
                                    unCheckedChildren={<StarOutlined />}
                                />
                            </Tooltip>
                            <br />
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                Required
                            </Text>
                        </div>
                    </Space>
                )
            },
            {
                title: 'Help & Validation',
                key: 'help',
                width: 200,
                render: (_: any, record: FieldMetadataDTO) => (
                    <div>
                        {record.helpText && (
                            <div style={{ marginBottom: 8 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                                    {record.helpText}
                                </Text>
                            </div>
                        )}
                        {record.placeholder && (
                            <div style={{ marginBottom: 8 }}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    Placeholder: {record.placeholder}
                                </Text>
                            </div>
                        )}
                        {record.validationRules && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    Validation: {record.validationRules}
                                </Text>
                            </div>
                        )}
                    </div>
                )
            }
        ];

        return (
            <Table
                dataSource={fields}
                columns={fieldColumns}
                rowKey="fieldName"
                pagination={false}
                size="small"
                scroll={{ x: 1000 }}
                bordered
            />
        );
    };

    const createTabItems = () => {
        if (!groupedFieldConfig?.fieldGroups?.length) {
            return [
                {
                    key: 'no-config',
                    label: 'No Configuration',
                    children: (
                        <Alert
                            message="No Field Configuration Available"
                            description={`No field configuration found for ${customerType} customer type. Please check the backend configuration.`}
                            type="warning"
                            showIcon
                        />
                    )
                }
            ];
        }

        return groupedFieldConfig.fieldGroups
            .filter(group => group.isVisible)
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
            .map(group => ({
                key: group.groupKey || '',
                label: (
                    <span>
                        <span style={{ marginRight: 8 }}>
                            {group.icon || '📝'}
                        </span>
                        {group.groupName}
                        {group.isRequired && (
                            <Badge 
                                count="*" 
                                style={{ 
                                    backgroundColor: '#ff4d4f',
                                    marginLeft: 4,
                                    fontSize: '10px'
                                }} 
                            />
                        )}
                        <Badge 
                            count={group.fields?.length || 0} 
                            style={{ 
                                backgroundColor: '#1890ff',
                                marginLeft: 4
                            }} 
                        />
                    </span>
                ),
                children: (
                    <div>
                        {group.description && (
                            <Alert
                                message={group.description}
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                                closable
                            />
                        )}
                        {renderFieldTable(group.fields || [], group.groupKey || '')}
                    </div>
                )
            }));
    };


    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text type="secondary">Loading field configuration...</Text>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="Error Loading Field Configuration"
                description="Failed to load field configuration. Please try again."
                type="error"
                showIcon
                action={
                    <Space>
                        <Button size="small" onClick={() => refetchTemplate()}>
                            Retry Template
                        </Button>
                        {categoryId && (
                            <Button size="small" onClick={() => refetchCategory()}>
                                Retry Category
                            </Button>
                        )}
                    </Space>
                }
            />
        );
    }

    // Show empty state for new categories until template is fetched
    if (mode === 'create' && !currentConfiguration) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Alert
                    message="No Field Configuration"
                    description={
                        <div>
                            <p>This new category doesn't have any field configuration yet.</p>
                            <p>Click "Fetch Template" to load the default field template for <strong>{customerType}</strong> customer type, then customize as needed.</p>
                        </div>
                    }
                    type="info"
                    showIcon
                    action={
                        <Button
                            type="primary"
                            icon={<FileOutlined />}
                            onClick={handleFetchTemplate}
                            loading={isLoadingTemplate}
                        >
                            Fetch Template for {customerType}
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Text type="secondary">
                        Customer Type: <Text strong>{customerType}</Text>
                        {groupedFieldConfig && (
                            <>
                                {' | '}
                                Total Fields: <Text strong>{groupedFieldConfig.totalFields}</Text>
                                {' | '}
                                Required Fields: <Text strong>{groupedFieldConfig.requiredFields}</Text>
                            </>
                        )}
                    </Text>
                </div>
                <Space>
                    {/* Configuration Status Indicator */}
                    {categoryId && (
                        <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {isUsingTemplate ? (
                                    <><FileOutlined style={{ color: '#1890ff' }} /> Using Template</>
                                ) : (
                                    <><SaveOutlined style={{ color: '#52c41a' }} /> Custom Configuration</>
                                )}
                            </Text>
                        </div>
                    )}
                    
                    {/* Fetch Template Button - Frontend only action */}
                    <Button
                        type="default"
                        size="small"
                        icon={<FileOutlined />}
                        onClick={handleFetchTemplate}
                        disabled={mode === 'view' || isLoadingTemplate}
                        loading={isLoadingTemplate}
                    >
                        {mode === 'create' ? 'Fetch Template' : 'Reset to Template'}
                    </Button>
                    
                    {/* Configuration will be saved automatically when the category is saved */}
                    
                    <Button
                        type="text"
                        size="small"
                        onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                        disabled={mode === 'view'}
                    >
                        {(showAdvancedOptions ? 'Hide' : 'Show') + ' Advanced Options'}
                    </Button>
                    
                    {Boolean(error) && (
                        <Button 
                            size="small" 
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                refetchTemplate();
                                if (categoryId) refetchCategory();
                            }}
                        >
                            Refresh
                        </Button>
                    )}
                </Space>
            </div>
            
            {/* UI Configuration Panel */}
            {showAdvancedOptions && groupedFieldConfig && (
                <Card 
                    title={
                        <Space>
                            <SettingOutlined />
                            <span>UI Configuration</span>
                        </Space>
                    }
                    size="small" 
                    style={{ marginBottom: 16 }}
                >
                    <Collapse size="small" ghost>
                        <Collapse.Panel 
                            header={
                                <Space>
                                    <AppstoreOutlined />
                                    <span>Group Settings</span>
                                </Space>
                            } 
                            key="group-settings"
                        >
                            <Row gutter={[16, 16]}>
                                {groupedFieldConfig.fieldGroups?.map((group) => (
                                    <Col span={24} key={group.groupKey}>
                                        <Card size="small" title={group.groupName}>
                                            <Row gutter={[16, 8]} align="middle">
                                                <Col span={6}>
                                                    <Space>
                                                        {group.isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                        <Text strong>Visibility:</Text>
                                                    </Space>
                                                </Col>
                                                <Col span={6}>
                                                    <Switch
                                                        checked={group.isVisible}
                                                        onChange={(checked) => handleGroupVisibilityChange(group.groupKey || '', checked)}
                                                        checkedChildren="Visible"
                                                        unCheckedChildren="Hidden"
                                                        disabled={mode === 'view'}
                                                    />
                                                </Col>
                                                <Col span={6}>
                                                    <Text strong>Rendering Mode:</Text>
                                                </Col>
                                                <Col span={6}>
                                                    <Select
                                                        value={group.uiConfig?.renderingMode || 'tabs'}
                                                        onChange={(value) => handleGroupRenderingModeChange(group.groupKey || '', value)}
                                                        style={{ width: '100%' }}
                                                        disabled={mode === 'view'}
                                                    >
                                                        <Select.Option value="tabs">
                                                            <Space>
                                                                <AppstoreOutlined />
                                                                Tabs
                                                            </Space>
                                                        </Select.Option>
                                                        <Select.Option value="wizard">
                                                            <Space>
                                                                <OrderedListOutlined />
                                                                Wizard
                                                            </Space>
                                                        </Select.Option>
                                                        <Select.Option value="inline">
                                                            <Space>
                                                                <InfoCircleOutlined />
                                                                Inline
                                                            </Space>
                                                        </Select.Option>
                                                    </Select>
                                                </Col>
                                            </Row>
                                            {group.uiConfig?.renderingMode === 'wizard' && (
                                                <Row gutter={[16, 8]} align="middle" style={{ marginTop: 8 }}>
                                                    <Col span={6}>
                                                        <Text strong>Wizard Orientation:</Text>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Select
                                                            value={group.uiConfig?.wizardOrientation || 'horizontal'}
                                                            onChange={(value) => handleGroupWizardOrientationChange(group.groupKey || '', value)}
                                                            style={{ width: '100%' }}
                                                            disabled={mode === 'view'}
                                                        >
                                                            <Select.Option value="horizontal">Horizontal</Select.Option>
                                                            <Select.Option value="vertical">Vertical</Select.Option>
                                                        </Select>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Text strong>Show Progress:</Text>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Switch
                                                            checked={group.uiConfig?.showProgress !== false}
                                                            onChange={(checked) => handleGroupProgressChange(group.groupKey || '', checked)}
                                                            size="small"
                                                            disabled={mode === 'view'}
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </Collapse.Panel>
                    </Collapse>
                </Card>
            )}
            
            <Tabs
                activeKey={activeTabKey}
                onChange={setActiveTabKey}
                items={createTabItems()}
                type="card"
                size="small"
            />
        </div>
    );
});

FieldConfigurationTab.displayName = 'FieldConfigurationTab';

export default FieldConfigurationTab;
